import { G } from '@mobily/ts-belt';
import Warning from '@mui/icons-material/Warning';
import { Box } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useGetList } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { GetSepaMandatesResponseItem } from '@Common/types/bo/sepa_mandates';

import { ShowSection } from '@sh4res/bo-common/components';
import {
  RadioButtonGroupInput,
  RadioButtonGroupInputChoices,
} from '@sh4res/bo-common/components/RadioButtonGroupInput';
import { TypographyTranslated } from '@sh4res/bo-common/components/TypographyTranslated';

const testId = 'BoExternalB2bVoluntaryPaymentCreatePaymentStep';

export const PaymentStep = ({ sepaOnly = false }: { sepaOnly?: boolean }) => {
  const { setValue } = useFormContext();

  const [userId, paymentMethod]: [number | null, string | undefined] = useWatch({
    name: ['user_id', 'payment_method'],
  });

  const { data: sepaMandates } = useGetList<GetSepaMandatesResponseItem>(
    'sepa_mandates',
    {
      filter: { user_id: userId as number, is_archived: false },
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'id', order: 'DESC' },
    },
    { enabled: G.isNotNullable(userId) },
  );

  // An employee-savings SEPA mandate is usable once it is signed (and not archived, already
  // filtered server-side).
  const activeSepaMandate = useMemo(
    () =>
      (sepaMandates ?? []).find(
        (mandate) => mandate.investing_context === 'savings' && G.isNotNullable(mandate.signed_at),
      ),
    [sepaMandates],
  );

  const hasActiveSepa = G.isNotNullable(activeSepaMandate);

  // Clear a stale SEPA selection if it is no longer available for the selected employee.
  useEffect(() => {
    if (!hasActiveSepa && paymentMethod === 'sepa-direct-debit') {
      setValue('payment_method', null);
    }
  }, [hasActiveSepa, paymentMethod, setValue]);

  const noMandate = sepaOnly && G.isNotNullable(sepaMandates) && !hasActiveSepa;

  // Exposed to the stepper so it can disable the next button, the mandate status being only known here.
  useEffect(() => {
    if (sepaOnly) {
      setValue('has_valid_mandate', G.isNotNullable(sepaMandates) ? hasActiveSepa : undefined);
    }
  }, [sepaOnly, sepaMandates, hasActiveSepa, setValue]);

  const choices: RadioButtonGroupInputChoices = [
    ...(sepaOnly
      ? []
      : [
          {
            id: 'regular-bank',
            name: 'resources.bo_common.b2b.voluntary_payment_create.choices.payment_method.regular_bank',
          },
        ]),
    ...(hasActiveSepa
      ? [
          {
            id: 'sepa-direct-debit',
            name: 'resources.bo_common.b2b.voluntary_payment_create.choices.payment_method.sepa_direct_debit_with_account',
            options: { last_digits: activeSepaMandate.account_number.slice(-4) },
          },
        ]
      : []),
  ];

  return (
    <ShowSection
      leftAligned
      label="resources.bo_common.b2b.voluntary_payment_create.sections.payment"
      noCard={true}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {noMandate && (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
            <Warning style={{ color: '#FF6213', marginTop: '2px' }} />
            <TypographyTranslated
              data-testid={`${testId}_NoMandateMessage`}
              label="resources.bo_common.b2b.recurring_investments.create.messages.no_valid_mandate"
            />
          </Box>
        )}
        {/* Kept mounted when hidden so the required validation still blocks the next step */}
        <Box sx={{ display: noMandate ? 'none' : undefined }}>
          <RadioButtonGroupInput
            data-testid={`${testId}_PaymentMethodInput`}
            source="payment_method"
            required
            choices={choices}
          />
        </Box>
      </Box>
    </ShowSection>
  );
};
